import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  ArrowRight,
  Loader2,
  Sparkles,
} from '@digital-twins/design-system/icons'
import { Button } from '@/components/ui/button'
import {
  AUDIENCE_WIZARD_BUTTON_CLASS,
  AUDIENCE_WIZARD_BUTTON_VARIANT,
} from '@/lib/constants/audience-wizard'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Text } from '@/components/ui/Text'
import { cn } from '@/lib/utils'
import { AGE_BELOW_FIRST_BAND_LABEL } from '@digital-twins/shared-types'
import { populationsApi } from '@/lib/api/client'
import {
  autoDetectDemographicValueMap,
  DEMOGRAPHIC_CANONICAL_VALUES,
  VALUE_MAP_BLANK_SENTINEL,
  VALUE_MAP_RANDOM_TARGET,
} from '../../lib/demographic-value-maps'
import i18n from '@/i18n'
import { COUNTRIES } from '@/lib/constants/demographics'
import { localizedCountryName } from '@/lib/utils/localized-country-name'
import { CountrySearchSelect } from './CountrySearchSelect'
import type { ValueMappingEditorProps } from '@/types/components/audiences/mapping-components'

/**
 * Resolve what to display in the "Raw value" column.
 *
 * Priority:
 *  1. Blank sentinel -> "(empty / missing)".
 *  2. `valueLabels[raw]` if backend populated a code -> label map (e.g. SPSS value labels).
 *  3. For `std_geo_country`, derive display from the persisted ISO mapping
 *     (`initialValueMap[raw]`) via the shared `COUNTRIES` list. Uses the saved
 *     value (not the draft) so the column stays stable while the user edits.
 *  4. Fall back to the raw value.
 *
 * The mapping-key space (workingMap keys) is always the raw code; this helper
 * never affects the PATCH payload.
 */
export function resolveRawValueDisplayLabel(args: {
  rawValue: string
  canonicalId: string
  valueLabels: Record<string, string> | null | undefined
  initialValueMap: Record<string, string> | null | undefined
}): string {
  const { rawValue, canonicalId, valueLabels, initialValueMap } = args
  if (rawValue === VALUE_MAP_BLANK_SENTINEL) {
    return String(i18n.t('audienceCreate.emptyMissing', { ns: 'app' }))
  }
  const fromValueLabels = valueLabels?.[rawValue]
  if (fromValueLabels && fromValueLabels.trim().length > 0) {
    if (canonicalId === 'std_dem_age' && /^\d{1,3}$/.test(rawValue)) {
      // Raw numeric ages (e.g. "28") are already human-readable; showing the
      // bucket label ("25-34") from SPSS/datamap value-labels is misleading
      // because it looks identical to the canonical target on the right side.
    } else {
      return fromValueLabels
    }
  }
  if (canonicalId === 'std_geo_country') {
    const persistedIso = initialValueMap?.[rawValue]
    if (persistedIso && persistedIso !== VALUE_MAP_RANDOM_TARGET) {
      const match = COUNTRIES.find((c) => c.code === persistedIso)
      if (match?.name) {
        return localizedCountryName(match.code, i18n.language)
      }
    }
  }
  return rawValue
}

function getCanonicalOptions(canonicalId: string): string[] {
  switch (canonicalId) {
    case 'std_dem_gender':
      return DEMOGRAPHIC_CANONICAL_VALUES.gender
    case 'std_dem_age':
      return DEMOGRAPHIC_CANONICAL_VALUES.age
    case 'std_dem_ethnicity':
      return DEMOGRAPHIC_CANONICAL_VALUES.ethnicity
    default:
      return []
  }
}

function normalizeLegacyIgnoredValue(canonicalId: string, value: string): string {
  if (value.trim().toLowerCase() !== 'ignore') {
    return value
  }
  // Legacy "Ignore" should migrate to a meaningful target for demographics.
  if (
    canonicalId === 'std_dem_gender' ||
    canonicalId === 'std_dem_age' ||
    canonicalId === 'std_geo_country'
  ) {
    return VALUE_MAP_RANDOM_TARGET
  }
  return ''
}

/**
 * Merges persisted `valueMap` with re-detected heuristics. We only treat the
 * saved map as "complete" when every current distinct row has a non-empty
 * target. Otherwise a stale map from a different source (or leftover keys) can
 * set `hasOverlap` true for a **single** raw code, skip re-detection, and
 * leave every other "Map to" cell blank — e.g. after changing source and back.
 */
export function buildWorkingMapFromProps(args: {
  canonicalId: string
  initialValueMap: Record<string, string>
  distinctValues: string[]
}): Record<string, string> {
  const { canonicalId, initialValueMap, distinctValues } = args
  const normalizedInitial = Object.fromEntries(
    Object.entries(initialValueMap).map(([raw, mapped]) => [
      raw,
      normalizeLegacyIgnoredValue(canonicalId, mapped ?? ''),
    ]),
  )
  const next: Record<string, string> = {}
  for (const value of distinctValues) {
    next[value] = normalizedInitial[value] ?? ''
  }
  const noInitialRows = Object.keys(initialValueMap).length === 0
  const hasFullSavedCoverage =
    distinctValues.length > 0 &&
    distinctValues.every(
      (v) => (normalizedInitial[v] ?? '').trim().length > 0,
    )
  if (noInitialRows || !hasFullSavedCoverage) {
    const detected = autoDetectDemographicValueMap(canonicalId, distinctValues)
    for (const value of distinctValues) {
      if ((next[value] ?? '').trim() !== '') {
        continue
      }
      const det = detected[value]
      if (det !== undefined && String(det).trim() !== '') {
        next[value] = det
      } else if (det !== undefined) {
        next[value] = det ?? ''
      }
    }
  }
  // A saved "Sample from distribution" on ages 13–17 was the only choice
  // before the under-18 band existed. Show that band instead of resampling.
  if (canonicalId === 'std_dem_age') {
    const detected = autoDetectDemographicValueMap(canonicalId, distinctValues)
    for (const value of distinctValues) {
      const saved = (next[value] ?? '').trim()
      if (
        saved.toLowerCase() === VALUE_MAP_RANDOM_TARGET.toLowerCase() &&
        detected[value] === AGE_BELOW_FIRST_BAND_LABEL
      ) {
        next[value] = AGE_BELOW_FIRST_BAND_LABEL
      }
    }
  }
  return next
}

function ValueMappingRow({
  canonicalId,
  canonicalValue,
  disabled,
  options,
  onChange,
}: {
  canonicalId: string
  canonicalValue: string
  disabled: boolean
  options: string[]
  onChange: (nextValue: string) => void
}) {
  const { t } = useTranslation('app')
  if (canonicalId === 'std_geo_country') {
    return (
      <div className="flex items-center gap-1.5 flex-1 basis-0 min-w-0">
        <CountrySearchSelect
          value={canonicalValue}
          onChange={onChange}
          disabled={disabled}
          specialValueDisplay={{
            wireValue: VALUE_MAP_RANDOM_TARGET,
            label: t('audienceCreate.sampleFromDistribution'),
          }}
        />
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-7 px-2 text-[11px]"
          disabled={disabled || canonicalValue.length === 0}
          onClick={() => onChange('')}
        >
          {t('audienceCreate.clear')}
        </Button>
      </div>
    )
  }

  return (
    <div className="flex-1 basis-0 min-w-0">
      <Select
        value={canonicalValue || '__unset__'}
        onValueChange={(value) => onChange(value === '__unset__' ? '' : value)}
        disabled={disabled}
      >
        <SelectTrigger className="h-9 text-base text-foreground dark:text-white w-full">
          <SelectValue placeholder={t('audienceCreate.selectEllipsis')} />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="__unset__" className="text-base text-muted-foreground">
            {t('audienceCreate.selectEllipsis')}
          </SelectItem>
          {options.map((option) => (
            <SelectItem key={option} value={option} className="text-base">
              {option === VALUE_MAP_RANDOM_TARGET
                ? t('audienceCreate.sampleFromDistribution')
                : option}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  )
}

export function ValueMappingEditor({
  canonicalId,
  variableLabel: _variableLabel,
  sourceColumn,
  initialValueMap,
  distinctValues,
  valueLabels = null,
  previewRows = [],
  allColumnNames = [],
  disabled = false,
  onDirtyChange,
  onBusyChange,
  applyLabel,
  onApply,
  renderActions,
  renderToolbarButtons,
}: ValueMappingEditorProps) {
  const { t } = useTranslation('app')
  const resolvedApplyLabel = applyLabel ?? t('audienceCreate.applyMapping')
  
  // builtInitial is what buildWorkingMapFromProps produces from the current props:
  // the server-persisted map with auto-detection filling any gaps. workingMap is
  // initialized to this value and returns to it whenever the field/column changes.
  // hasDraftChanges compares workingMap against builtInitial (not the raw
  // initialValueMap) so that auto-detected fill-ins do NOT count as user edits.
  // Previously the comparison was against initialValueMap, which caused a
  // permanent false-positive dirty state when initialValueMap was empty {} but
  // buildWorkingMapFromProps had auto-filled e.g. birth-years → age bands.
  const builtInitial = useMemo(
    () => buildWorkingMapFromProps({ canonicalId, initialValueMap, distinctValues }),
    [canonicalId, distinctValues, initialValueMap],
  )
  const [workingMap, setWorkingMap] = useState<Record<string, string>>(
    () => buildWorkingMapFromProps({ canonicalId, initialValueMap, distinctValues }),
  )
  const [llmLoading, setLlmLoading] = useState(false)
  const [applyPending, setApplyPending] = useState(false)

  useEffect(() => {
    setWorkingMap(
      buildWorkingMapFromProps({ canonicalId, initialValueMap, distinctValues }),
    )
    setApplyPending(false)
  }, [canonicalId, distinctValues, initialValueMap, sourceColumn])

  const entries = useMemo(() => Object.entries(workingMap), [workingMap])
  const options = useMemo(() => getCanonicalOptions(canonicalId), [canonicalId])
  const isBusy = disabled || llmLoading || applyPending
  const unmappedEntries = entries.filter(([, value]) => !value)
  const unmappedCount = unmappedEntries.length
  const hasDraftChanges = useMemo(() => {
    if (Object.keys(workingMap).some((k) => !distinctValues.includes(k))) {
      return true
    }
    return distinctValues.some(
      (key) => (workingMap[key] ?? '') !== (builtInitial[key] ?? ''),
    )
  }, [builtInitial, distinctValues, workingMap])

  const totalCount = entries.length

  useEffect(() => {
    onDirtyChange?.(hasDraftChanges)
  }, [hasDraftChanges, onDirtyChange])

  useEffect(() => {
    onBusyChange?.(isBusy)
  }, [isBusy, onBusyChange])

  const handleEntryChange = (rawValue: string, canonicalValue: string) => {
    setWorkingMap((prev) => ({ ...prev, [rawValue]: canonicalValue }))
  }

  const handleAutoDetect = () => {
    const detected = autoDetectDemographicValueMap(canonicalId, distinctValues)
    for (const value of distinctValues) {
      if (!(value in detected)) detected[value] = ''
    }
    setWorkingMap(detected)
  }

  const handleAutoFill = async () => {
    setLlmLoading(true)
    try {
      const unmapped = unmappedEntries.map(([raw]) => raw)
      if (unmapped.length === 0) return
      const { suggestedMappings } = await populationsApi.inferDemographicValueMap({
        canonicalId,
        sourceColumn,
        unmappedValues: unmapped,
        rowSamples: previewRows.slice(0, 10),
        allColumnNames,
      })
      setWorkingMap((prev) => ({ ...prev, ...suggestedMappings }))
    } catch {
      // silently fail — user can map manually
    } finally {
      setLlmLoading(false)
    }
  }

  const handleApply = async () => {
    setApplyPending(true)
    try {
      await onApply(sourceColumn, workingMap)
    } finally {
      setApplyPending(false)
    }
  }

  const handleUndo = () => {
    setWorkingMap(
      buildWorkingMapFromProps({ canonicalId, initialValueMap, distinctValues }),
    )
  }

  const toolbarInHeader = renderToolbarButtons
    ? renderToolbarButtons({
      onAutoDetect: handleAutoDetect,
      onAutoFill: () => void handleAutoFill(),
      llmLoading,
      unmappedCount,
      isBusy,
    })
    : null

  return (
    <div className="space-y-4">
      {totalCount === 0 ? (
        <div className="py-8 text-center">
          <Text size="sm" color="muted">
            {t('audienceCreate.noValuesToMap')}
          </Text>
        </div>
      ) : (
        <div className="max-h-[360px] overflow-y-auto">
          <div className="space-y-1.5 pr-3">
            <div className="flex items-center gap-2 border-b border-border/40 pb-1.5">
              <Text size="sm" color="muted" className="flex-1 basis-0 font-medium">
                {t('audienceCreate.rawValue')}
              </Text>
              <div className="w-4 shrink-0" aria-hidden />
              <Text size="sm" color="muted" className="flex-1 basis-0 font-medium">
                {t('audienceCreate.mapTo')}
              </Text>
              {renderToolbarButtons
                ? toolbarInHeader
                : unmappedEntries.length > 0 && (
                  <button
                    type="button"
                    onClick={() => void handleAutoFill()}
                    disabled={isBusy}
                    className="flex shrink-0 items-center gap-1 text-xs text-muted-foreground hover:text-foreground dark:hover:text-white transition-colors disabled:opacity-40"
                  >
                    {llmLoading ? (
                      <Loader2 className="h-3 w-3 animate-spin" />
                    ) : (
                      <Sparkles className="h-3 w-3" />
                    )}
                    {t('audienceCreate.fillWithAi')}
                  </button>
                )}
            </div>

            {entries.map(([rawValue, canonicalValue]) => (
              <div key={rawValue} className="flex items-center gap-2 py-0.5">
                {(() => {
                  const rawValueLabel = resolveRawValueDisplayLabel({
                    rawValue,
                    canonicalId,
                    valueLabels,
                    initialValueMap,
                  })
                  const showRawCodeInTitle =
                    rawValueLabel !== rawValue &&
                    rawValue !== VALUE_MAP_BLANK_SENTINEL
                  const titleText = showRawCodeInTitle
                    ? `${rawValueLabel} (raw: ${rawValue})`
                    : rawValueLabel
                  return (
                    <span
                      className="flex-1 basis-0 truncate text-base text-foreground dark:text-white"
                      title={titleText}
                    >
                      {rawValueLabel}
                    </span>
                  )
                })()}
                <ArrowRight className="h-4 w-4 shrink-0 text-muted-foreground/40" />
                <ValueMappingRow
                  canonicalId={canonicalId}
                  canonicalValue={canonicalValue}
                  disabled={isBusy}
                  options={options}
                  onChange={(nextValue) => handleEntryChange(rawValue, nextValue)}
                />
              </div>
            ))}
          </div>
        </div>
      )}

      {renderActions ? (
        renderActions({
          onApply: () => void handleApply(),
          onUndo: handleUndo,
          isApplying: applyPending,
          isDirty: hasDraftChanges,
          isBusy,
        })
      ) : (
        <div className="flex justify-center pt-5">
          <Button
            type="button"
            variant={AUDIENCE_WIZARD_BUTTON_VARIANT}
            onClick={() => void handleApply()}
            disabled={isBusy}
            className={cn('h-10 px-4', AUDIENCE_WIZARD_BUTTON_CLASS)}
          >
            {applyPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {t('audienceCreate.saving')}
              </>
            ) : (
              resolvedApplyLabel
            )}
          </Button>
        </div>
      )}
    </div>
  )
}
